'use client';

import React, { useCallback, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';

import { Link } from '@/i18n/routing';

import { ELanguage } from '@/enums/language.enum';
import type { IMenuItem } from '@/interfaces/collection.interface';

type TNavLinksProps = {
  menus: IMenuItem[];
  setMobileMenuOpen?: (isOpen: boolean) => void;
};

const MobileNavLinks: React.FC<TNavLinksProps> = ({ menus, setMobileMenuOpen }) => {
  const pathname = usePathname();
  const locale: ELanguage = useLocale() as ELanguage;
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const handleMouseEnter = useCallback((index: number) => setHoveredIndex(index), []);
  const handleMouseLeave = useCallback(() => setHoveredIndex(null), []);
  const handleClick = useCallback(() => setMobileMenuOpen?.(false), [setMobileMenuOpen]);

  return (
    <ul className="flex flex-col gap-5 lg:flex-row">
      {menus?.map((menu, index) => (
        <div key={`menu_${index}`}>
          <div onMouseEnter={() => handleMouseEnter(index)} onMouseLeave={handleMouseLeave}>
            <Link
              href={menu?.isMenu ? `/shop?collection=${menu.slug}` : `/shop?menu=${menu.slug}`}
              className={`hover:text-clr-20 ${pathname === menu.slug ? 'text-clr-20' : 'text-black'}`}
              onClick={handleClick}
            >
              {menu.title?.[locale] || menu.title?.[ELanguage.ENGLISH]}
            </Link>
          </div>
          {menu?.categories?.length > 0 && (
            <div
              className={`absolute z-50 ${hoveredIndex === index ? 'block' : 'hidden'}`}
              onMouseEnter={() => handleMouseEnter(index)}
              onMouseLeave={handleMouseLeave}
            >
              {menu?.categories?.map((category, catIndex) => (
                <div key={catIndex}>
                  <h3>{category.title[locale]}</h3>
                  {category.collections?.map((sub, subIndex) => (
                    <Link key={subIndex} href={`/shop?category=${category.slug}&collection=${sub.slug}`}>
                      {sub.title[locale]}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      <div>
        <Link href="/stories" className="hover:text-clr-20" onClick={handleClick}>
          Stories
        </Link>
      </div>
    </ul>
  );
};

export default MobileNavLinks;
